import { Spinner } from "@chakra-ui/react";
import { useIsMenuCollapsed } from "newui/hooks/useMenuStatus";
import React, { ReactNode, useState } from "react";
import { FiMenu, FiX } from "react-icons/fi";

type ButtonPosition = "left" | "center" | "right";

interface BaseFloatingButtonProps {
	className?: string;
	isDisabled?: boolean;
	isLoading?: boolean;
	loadingText?: string;
	position?: ButtonPosition;
	text?: string;
}

interface FloatingButtonWithChildrenProps extends BaseFloatingButtonProps {
	children: ReactNode;
	onClick?: never;
}

interface FloatingButtonWithoutChildrenProps extends BaseFloatingButtonProps {
	children?: null;
	onClick: () => void;
}

type FloatingButtonProps =
	| FloatingButtonWithChildrenProps
	| FloatingButtonWithoutChildrenProps;

const FloatingButton: React.FC<FloatingButtonProps> = ({
	className = "",
	children = null,
	isDisabled = false,
	isLoading = false,
	loadingText,
	onClick,
	position = "right",
	text = "Save",
}) => {
	const isSideBarCollapsed = useIsMenuCollapsed();
	const [isMobileOpen, setIsMobileOpen] = useState(false);

	const getLeftPosition = () => {
		if (isSideBarCollapsed) {
			return "md:left-16";
		}
		return "md:left-72";
	};

	// Tailwind classes for desktop positioning
	const positionClasses: Record<ButtonPosition, string> = {
		left: getLeftPosition(),
		center:
			"md:left-1/2 md:-translate-x-1/2 lg:left-1/2 lg:-translate-x-1/2 xl:left-1/2 xl:-translate-x-1/2",
		right: "md:right-4 lg:right-8 xl:right-12",
	};

	return (
		<>
			{/* Desktop */}
			<div
				className={`hidden md:block fixed z-[1000] w-full px-4 sm:w-auto ${positionClasses[position]} ${className}`}
			>
				{children || (
					<button
						onClick={onClick}
						disabled={isDisabled}
						className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-lg transition-colors hover:bg-blue-700 disabled:pointer-events-none disabled:border-none disabled:bg-gray-300 disabled:text-gray-400 sm:w-auto"
					>
						{isLoading && (
							<Spinner
								size="sm"
								color="white"
							/>
						)}

						{isLoading ? (loadingText ?? text) : text}
					</button>
				)}
			</div>

			{/* Mobile */}
			<div className="fixed bottom-4 left-4 z-[1000] md:hidden">
				<div
					className={`mb-2 transition-all ${isMobileOpen
						? "pointer-events-auto opacity-100"
						: "pointer-events-none opacity-0"
						}`}
				>
					{children}
				</div>

				<button
					type="button"
					onClick={() => setIsMobileOpen((prev) => !prev)}
					className="rounded-full bg-blue-600 p-4 text-white shadow-lg"
				>
					{isMobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
				</button>
			</div>
		</>
	);
};

export default FloatingButton;
